import { isUserAllowed } from '@ynap/user-state';
import isProductSoldOut from './isProductSoldOut';
import isProductOnFinalSale from './isProductOnFinalSale';

export const getBuyableStatus = ({
    selectedProduct = {},
    price,
    user,
    locale,
    messages,
    productUrl,
    pdpMessageConfig,
    userCountry,
    defaultSalesCatalog,
    beautyExclude,
    priceThresholds,
}) => {
    const { buyable, notStockedOnline, shippingRestricted, restrictedToSegments } = selectedProduct;
    const accessDenied = restrictedToSegments?.length && !isUserAllowed(restrictedToSegments, user);
    const isLuxWatches = findCategory(selectedProduct, 'Luxury-Watches');
    const luxWatches_Unbuyable = findAttribute(selectedProduct, 'LuxWatches_Unbuyable', 'TRUE');
    const notBuyable = findAttribute(selectedProduct, 'NOT_BUYABLE', 'NOT_BUYABLE');
    const hasUnbuyableAttribute = luxWatches_Unbuyable || notBuyable;
    const soldOutOnline = isProductSoldOut(selectedProduct);
    const canAddToBag = !hasUnbuyableAttribute && !accessDenied && !!buyable && !notStockedOnline && !shippingRestricted && !soldOutOnline;
    const isBeautyRestricted = findCategory(selectedProduct, 'Beauty') && beautyExclude?.includes(userCountry);
    const isHazMat = findAttribute(selectedProduct, 'Hazardous Materials', 'Yes');
    const isLithiumBattery = findAttribute(selectedProduct, 'LithiumBattery', 'Yes');
    const finalSale = isProductOnFinalSale(selectedProduct);
    const preOrder = selectedProduct.preOrder;

    return {
        finalSale,
        canAddToBag,
        canAddToWishlist: canAddToWishlist({
            notStockedOnline,
            hasUnbuyableAttribute,
        }),
        notification: getMessage(
            {
                isLuxWatches,
                luxWatches_Unbuyable,
                notBuyable,
                buyable,
                notStockedOnline,
                shippingRestricted,
                preOrder,
                soldOutOnline,
                isBeautyRestricted,
                isHazMat,
                isLithiumBattery,
                finalSale,
                accessDenied,
            },
            price,
            user,
            locale,
            messages,
            productUrl,
            pdpMessageConfig,
            defaultSalesCatalog,
            userCountry,
            priceThresholds,
        ),
    };
};

const getMessage = (
    {
        isLuxWatches,
        luxWatches_Unbuyable,
        notBuyable,
        notStockedOnline,
        shippingRestricted,
        buyable,
        soldOutOnline,
        isBeautyRestricted,
        isHazMat,
        isLithiumBattery,
        finalSale,
        accessDenied,
        preOrder,
    },
    price,
    user,
    locale,
    messages,
    productUrl,
    pdpMessageConfig = {},
    defaultSalesCatalog,
    userCountry,
    priceThresholds,
) => {
    const { enable = false, displayCountries, dispalySalesCatalog } = pdpMessageConfig;
    const { contactUsCTA } = messages || {};
    const { personalShopper, highPP, eip } = contactUsCTA || {};

    if (enable && buyable) {
        //if displayCountries or dispalySalesCatalog is specified, fallback to other message if it doesnt matches to the current country/catalog
        if ((displayCountries && !displayCountries?.includes(userCountry)) || (dispalySalesCatalog && !dispalySalesCatalog?.includes(defaultSalesCatalog))) {
        }

        //if pdp message is enabled, displayCountries/dispalySalesCatalog not defined, or displayCountries/dispalySalesCatalog matches to the current country/catalog
        else {
            return { message: messages?.product?.genericMessage() };
        }
    }
    if (luxWatches_Unbuyable || notBuyable) {
        return {
            contactUsCTA: personalShopper,
        };
    }

    //shipping restricted
    if (shippingRestricted) {
        return {
            message: messages?.product?.shippingRestriction({ productUrl }),
            type: 'countryShippingRestricted',
            productUrl,
            eip: {
                contactUsCTA: eip?.shippingRestricted,
            },
        };
    }

    //do not stock on line
    if (notStockedOnline) {
        return {
            message: messages?.product.notStockedOnLine(),
            eip: {
                contactUsCTA: eip?.notStockedOnline,
            },
        };
    }

    // segment (eg. EIP) restricted
    if (accessDenied) {
        return {
            message: messages?.product?.restrictedToEip(),
        };
    }

    //sold out online
    if (soldOutOnline) {
        return {
            message: messages?.product?.soldOut(),
            eip: {
                contactUsCTA: eip?.soldOutOnline,
            },
        };
    }

    //default not buyable
    if (!buyable) {
        return {
            message: messages?.product?.buyableFalse(),
            eip: {
                contactUsCTA: eip?.buyableFalse,
            },
        };
    }

    // restricted for specified countries for beauty category
    if (isBeautyRestricted) {
        return {
            message: messages?.product?.beautyRestrictedMessage({ locale }),
        };
    }

    if (isHazMat || isLithiumBattery) {
        return {
            message: messages?.product?.shippingRestrictionsNotificationMessage({ locale }),
            type: 'shippingRestricted',
        };
    }

    if (preOrder) {
        return {
            message: messages?.product?.preOrderNotificationMessage({ locale }),
            type: 'preOrder',
        };
    }

    if (finalSale) {
        return {
            message: messages?.product?.finalSaleNotification(),
        };
    }

    // high price items
    if (!user?.isEip && !isLuxWatches && price?.finalPrice >= (price && priceThresholds?.[price.currency?.label])) {
        return {
            type: 'highPPItem',
            contactUsCTA: highPP,
            eip: {
                contactUsCTA: eip?.default,
            },
        };
    }

    // default for EIP user without any additional flag
    if (eip) {
        return {
            type: 'showContactUsCTABottom',
            eip: {
                contactUsCTA: eip?.default,
            },
        };
    }

    return {
        message: '',
        type: '',
        contactUsCTA: null,
        eip: {
            contactUsCTA: null,
        },
    };
};

//can add to wishlist when notStockedOnline and luxWatches_Unbuyable are both false
const canAddToWishlist = ({ hasUnbuyableAttribute }) => {
    return !hasUnbuyableAttribute;
};

const findAttribute = (selectedProduct, identifier, valueId) => {
    return selectedProduct?.allLevelsProductAttributes?.find((attribute) => {
        return attribute?.identifier === identifier && attribute.values?.[0]?.identifier === valueId;
    });
};

const findCategory = (selectedProduct, categoryLabel) => {
    return selectedProduct?.salesCategories?.find((category) => {
        // CategoryId can have different ids depending on the region and label is also localised
        // So we split the identifier and look there for the label since it's the same for all regions and languages ex: "10103__Beauty"
        return category?.identifier.split('__')[1] === categoryLabel;
    });
};
